import { TitleConfig } from '../types/title';

class TitleService {
  private static instance: TitleService;
  private currentConfig: TitleConfig = {};
  private originalTitle: string = document.title;

  static getInstance(): TitleService {
    if (!TitleService.instance) {
      TitleService.instance = new TitleService();
    }
    return TitleService.instance;
  }

  // 현재 타이틀 설정을 저장
  setCurrentConfig(config: TitleConfig): void {
    this.currentConfig = { ...config };
  }

  getCurrentConfig(): TitleConfig {
    return { ...this.currentConfig };
  }

  getOriginalTitle(): string {
    return this.originalTitle;
  }

  // 타이틀 API URL 생성
  buildTitleApiUrl(params: TitleConfig): string {
    const apiUrl = process.env.REACT_APP_CAMPUS_API_BASE_URL || '';
    const queryString = new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();
    
    return `${apiUrl}/api/page-title${queryString ? '?' + queryString : ''}`;
  }

  // 페이지별 기본 타이틀 매핑
  getDefaultPageTitle(pathname: string): string {
    const pageMap: Record<string, string> = {
      '/': '홈',
      '/class': '강의',
      '/class/category': '강의 카테고리',
      '/archive': '아카이브',
      '/community': '커뮤니티',
      '/coffee-chat': '커피챗',
      '/search': '검색',
      '/mypage': '마이페이지',
      '/login': '로그인',
      '/signup': '회원가입',
      '/admin': '관리자',
    };

    // 정확한 매칭 시도
    if (pageMap[pathname]) {
      return pageMap[pathname];
    }

    // 패턴 매칭 시도
    if (pathname.startsWith('/class/')) {
      return '강의';
    }
    if (pathname.startsWith('/community/')) {
      return '커뮤니티';
    }
    if (pathname.startsWith('/coffee-chat/')) {
      return '커피챗';
    }
    if (pathname.startsWith('/mypage/')) {
      return '마이페이지';
    }
    if (pathname.startsWith('/admin/')) {
      return '관리자';
    }

    return '';
  }

  // 로컬 타이틀 생성 (백엔드 실패시 사용)
  generateTitle(config: TitleConfig): string {
    const {
      baseName = 'Campus',
      pageName,
      userName,
      notificationCount,
      company,
      isAdmin,
      customTitle,
      separator = ' - '
    } = config;

    if (customTitle) {
      return customTitle;
    }

    const parts: string[] = [];

    // 알림 개수가 있으면 맨 앞에
    if (notificationCount && notificationCount > 0) {
      const displayCount = notificationCount > 99 ? '99+' : notificationCount.toString();
      parts.push(`(${displayCount})`);
    }

    // 관리자 표시
    if (isAdmin) {
      parts.push('[Admin]');
    }

    // 페이지 이름
    if (pageName) {
      parts.push(pageName);
    }

    // 베이스 이름 (회사명 또는 기본명)
    const baseTitle = company 
      ? company.charAt(0).toUpperCase() + company.slice(1)
      : baseName;
    
    parts.push(baseTitle);

    // 사용자 이름은 마지막에
    if (userName) {
      parts.push(`| ${userName}`);
    }

    return parts.join(' ');
  }

  // 기본 타이틀로 리셋
  resetToDefault(): void {
    document.title = this.originalTitle;
    this.currentConfig = {};
  }

  // 알림 개수만 업데이트
  updateNotificationCount(count: number): void {
    const newConfig = { ...this.currentConfig, notificationCount: count };
    this.setCurrentConfig(newConfig);
    
    // 타이틀 변경 이벤트 발생
    window.dispatchEvent(new CustomEvent('titleConfigChanged', { 
      detail: newConfig 
    }));
  }

  // 페이지 이름 업데이트
  updatePageName(pageName: string): void {
    const newConfig = { ...this.currentConfig, pageName };
    this.setCurrentConfig(newConfig);
    
    window.dispatchEvent(new CustomEvent('titleConfigChanged', { 
      detail: newConfig 
    }));
  }

  // 사용자 정보 업데이트
  updateUserInfo(userName: string, userId?: string): void {
    const newConfig = { ...this.currentConfig, userName, userId };
    this.setCurrentConfig(newConfig);
    
    window.dispatchEvent(new CustomEvent('titleConfigChanged', { 
      detail: newConfig 
    }));
  }
}

export default TitleService;
